import React, { useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, LinearProgress, TablePagination,
    MenuItem, Select, CircularProgress, Button, DialogActions,
    DialogContentText, Dialog, DialogTitle, DialogContent,
    TextField, useMediaQuery
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../../../../assets/logo.png';
import { toast } from "react-toastify";
import baseURL from "../../config";

// Custom COLORS for a Microsoft-like aesthetic
const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

// Component now receives `campaigns` and `onUpdate` as props
const CampaignsManagement = ({ campaigns, onUpdate }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [statusFilter, setStatusFilter] = useState("all");
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [reason, setReason] = useState("");
    const [isClosingCampaign, setIsClosingCampaign] = useState(false);

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    const handleOpenDialog = (campaignId) => {
        setSelectedCampaignId(campaignId);
        setReason("");
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCampaignId(null);
        setReason("");
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (event) => setStatusFilter(event.target.value);

    const handleForceClose = async () => {
        if (!selectedCampaignId || !reason.trim()) {
            toast.error("Please provide a reason for closing the campaign.");
            return;
        }

        setIsClosingCampaign(true);

        try {
            const response = await fetch(`${baseURL}/admin/campaigns/${selectedCampaignId}/close`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ reason }),
                credentials: 'include',
            });

            if (response.ok) {
                toast.success("Campaign closed successfully!");
                onUpdate(); // Call the parent's update function to re-fetch data
            } else {
                const errorData = await response.json();
                console.error("Failed to close campaign:", errorData);
                toast.error(errorData.message || "Failed to close campaign.");
            }
        } catch (error) {
            console.error("Error closing campaign:", error);
            toast.error("An error occurred while closing the campaign. Please try again.");
        } finally {
            setIsClosingCampaign(false);
            handleCloseDialog();
        }
    };

    const handleDownloadLog = async (campaignId, campaignName) => {
        try {
            const res = await fetch(`${baseURL}/admin/campaigns/${campaignId}/donors`, {
                credentials: 'include'
            });

            const data = await res.json();

            if (!Array.isArray(data) || data.length === 0) {
                toast.info("No donor data available for this campaign.");
                return;
            }

            const doc = new jsPDF();
            doc.addImage(logo, 'PNG', 10, 10, 46, 20);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text(`Donation Log for: ${campaignName}`, 15, 35);

            const tableData = data.map((donor, index) => [
                index + 1,
                donor.email,
                `UGX ${Number(donor.amount).toLocaleString()}`,
                new Date(donor.created_at).toLocaleString()
            ]);

            autoTable(doc, {
                startY: 45,
                head: [["#", "Email", "Donation", "Date"]],
                body: tableData,
                styles: { fontSize: 9, cellPadding: 2 },
                headStyles: {
                    fillColor: MS_COLORS.primary,
                    textColor: MS_COLORS.white,
                    fontStyle: 'bold',
                    halign: 'left',
                },
                bodyStyles: {
                    textColor: [50, 50, 50],
                    valign: 'middle'
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 15 },
                    1: { halign: 'left' },
                    2: { halign: 'left' },
                    3: { halign: 'left' },
                },
                didDrawPage: function (data) {
                    let str = "Page " + doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    let pageSize = doc.internal.pageSize;
                    let x = data.settings.margin.left;
                    let y = pageSize.height - 10;
                    doc.text(str, x, y);
                }
            });

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');

        } catch (err) {
            console.error("Error downloading donor log:", err);
            toast.error("Failed to download donor log.");
        }
    };

    const filteredCampaigns =
        statusFilter === "all"
            ? campaigns
            : campaigns.filter((campaign) => campaign.status === statusFilter);

    // No longer needs a loading state, as it's handled by the parent
    // The component handles an empty state if the `campaigns` prop is empty
    const noCampaigns = campaigns.length === 0;
    const noFilteredCampaigns = filteredCampaigns.length === 0;

    return (
        <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ color: "#1a1a2e", mb: 3, fontWeight: 800, fontSize: isSmallScreen ? "1.05rem" : "1.2rem", letterSpacing: "-0.3px" }}>
                Campaigns Management
            </Typography>

            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
                <Select
                    value={statusFilter}
                    onChange={handleFilterChange}
                    size="small"
                    sx={{
                        minWidth: 150,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
                        "& .MuiSelect-select": { color: MS_COLORS.text, fontSize: '0.875rem' }
                    }}
                >
                    <MenuItem value="all">All Campaigns</MenuItem>
                    <MenuItem value="open">Open Campaigns</MenuItem>
                    <MenuItem value="closed">Closed Campaigns</MenuItem>
                </Select>
            </Box>

            {noCampaigns ? (
                <Box sx={{ textAlign: "center", color: MS_COLORS.textSecondary, mt: 4, mb: 4, fontSize: "0.9rem", p: 3, border: `1px dashed ${MS_COLORS.border}`, borderRadius: 1, bgcolor: MS_COLORS.backgroundLight }}>
                    <Typography variant="body1">
                        No campaigns available.
                    </Typography>
                </Box>
            ) : noFilteredCampaigns ? (
                <Box sx={{ textAlign: "center", color: MS_COLORS.textSecondary, mt: 4, mb: 4, fontSize: "0.9rem", p: 3, border: `1px dashed ${MS_COLORS.border}`, borderRadius: 1, bgcolor: MS_COLORS.backgroundLight }}>
                    <Typography variant="body1">
                        No campaigns available matching the selected filter.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 0, overflowX: "auto", border: `1px solid ${MS_COLORS.border}` }}>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>User Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Campaign Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Raised (UGX)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Goal (UGX)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Progress</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((campaign) => {
                                    const progress = (campaign.raised / campaign.goal) * 100;
                                    return (
                                        <TableRow key={campaign.id} sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { backgroundColor: MS_COLORS.hoverBackground } }}>
                                            <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{campaign.owner_name}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{campaign.name}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>UGX {campaign.raised.toLocaleString()}</TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>UGX {campaign.goal.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: isSmallScreen ? 100 : 150 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={progress > 100 ? 100 : progress}
                                                        sx={{ flexGrow: 1, height: 8, borderRadius: 5, bgcolor: MS_COLORS.border, "& .MuiLinearProgress-bar": { bgcolor: MS_COLORS.accent } }}
                                                    />
                                                    <Typography sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{progress.toFixed(2)}%</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    {campaign.status === "open" ? (<CheckCircle sx={{ color: MS_COLORS.accent, fontSize: "small" }} />) : (<Cancel sx={{ color: MS_COLORS.red, fontSize: "small" }} />)}
                                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: MS_COLORS.text }}>
                                                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {campaign.status === "open" ? (
                                                    <Button size="small" variant="outlined" color="error" onClick={() => handleOpenDialog(campaign.id)} sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 1, borderColor: MS_COLORS.red, boxShadow: 0, color: MS_COLORS.red, "&:hover": { backgroundColor: MS_COLORS.red + '10', borderColor: MS_COLORS.red } }}>
                                                        Force Close
                                                    </Button>
                                                ) : (
                                                    <Button size="small" variant="contained" onClick={() => handleDownloadLog(campaign.id, campaign.name)} sx={{ textTransform: "none", fontSize: "0.75rem", boxShadow: 0, borderRadius: 1, backgroundColor: MS_COLORS.primary, "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9 } }}>
                                                        Donation Log
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredCampaigns.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions": { fontSize: "0.8rem", color: MS_COLORS.textSecondary } }}
                        />
                    </TableContainer>
                </>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}>
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>Confirm Force Close Campaign</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: "0.9rem", color: MS_COLORS.textSecondary, mb: 2 }}>
                        Are you sure you want to force close this campaign? An email notification will be sent to the user informing them about this action.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Closure"
                        variant="outlined"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        sx={{
                            mt: 2,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                                "& fieldset": { borderColor: MS_COLORS.border },
                                "&:hover fieldset": { borderColor: MS_COLORS.primary },
                                "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: 2 }
                            },
                            "& .MuiInputBase-input": { fontSize: "0.9rem", color: MS_COLORS.text },
                            "& .MuiInputLabel-root": { fontSize: "0.9rem", color: MS_COLORS.textSecondary }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ pb: 1, px: 3 }}>
                    <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: "4px", fontSize: "0.85rem", textTransform: "none", borderColor: MS_COLORS.border, boxShadow: 0, color: MS_COLORS.text, "&:hover": { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30', boxShadow: 0 } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleForceClose} variant="contained" disabled={isClosingCampaign} sx={{ backgroundColor: MS_COLORS.red, color: MS_COLORS.white, borderRadius: "4px", fontSize: "0.85rem", textTransform: "none", boxShadow: 0, "&:hover": { backgroundColor: MS_COLORS.red, opacity: 0.9 }, "&:disabled": { backgroundColor: MS_COLORS.border, color: MS_COLORS.textSecondary, boxShadow: 0 } }}>
                        {isClosingCampaign ? (<><CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />Closing...</>) : ("Confirm Close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CampaignsManagement;